/**
 * Кольцо листания: ответ `/api/demand/ranking`, его ключ, параметры и кэш.
 * Часть `lib/productNav` (нарезка 14.10.26) — публичные имена те же.
 *
 * КОЛЬЦО КЭШИРУЕТСЯ В ПАМЯТИ ВКЛАДКИ. Ответ один на всего поставщика, и
 * перелистав сорок товаров, мы сходим в базу один раз. В sessionStorage он не
 * едет намеренно: данные живут десятки миллисекунд запроса, а лежали бы до
 * конца дня, показывая порядок позавчерашнего кэша спроса.
 */

import type { WindowValue } from "@/lib/reorder";
import { apiFetch, query } from "@/lib/api";
import type { NavDir, NavFilter, NavMetric, NavPlatform } from "@/lib/nav/labels";
import { NEED_METRIC, type NavSettings, readsPlans, readsWindow } from "@/lib/nav/settings";

export type RingItem = {
  product_id: number;
  name: string;
  article: string | null;
  /** Прочерк — товар без единой строки за окно, а не ноль показателя. */
  value: number | null;
};

export type Ring = {
  meta: {
    supplier_id: number;
    supplier: string | null;
    from: string;
    to: string;
    metric: NavMetric;
    platform: NavPlatform;
    dir: NavDir;
    plan: NavFilter;
    need: NavFilter;
    /** Категории ОТВЕТА (`null` — все): ими подписан отбор и названа причина
     *  пустого набора. Из ответа, а не из настроек, — подпись обязана называть
     *  тот отбор, по которому посчитаны видимые числа. */
    cat: number[] | null;
    /** Цвета ОТВЕТА — тем же правилом, что категории. */
    color: number[] | null;
    total: number;
    ozon_from: string | null;
    cached_to: { wb: string | null; ozon: string | null };
    /** Окно уходимости «к заказу» и фильтра потребности; иначе `null`. */
    window_days: number | null;
    /** Ключ окна уходимости: `"60"` или свой период `A:B`. */
    window?: string | null;
    /** Товаров с «к заказу» больше нуля; у воронки `null`. */
    need_count: number | null;
  };
  items: RingItem[];
};

/** Ключ кольца. Окно уходимости входит в него ТОЛЬКО когда его читают — «к
 * заказу» или фильтр потребности: у воронки без фильтра переключатель карточки
 * на порядок не влияет, и перезапрос был бы пустым. Фильтры входят всегда. */
export function ringKey(supplierId: number, s: NavSettings, w: WindowValue): string {
  const порядок =
    s.metric === NEED_METRIC ? [s.metric] : [s.from, s.to, s.metric, s.platform, s.dir];
  // Категория и цвет входят в ключ всегда: без них вкладка отдала бы платья
  // там, где попросили клатчи, — ответ лежит в кэше по ключу настроек.
  return [supplierId, ...порядок, s.plan, s.need, s.cat ?? "-", s.color ?? "-", readsWindow(s) ? w : "-"].join(
    "|",
  );
}

/** Запись кэша помнит, чьё это кольцо и зависит ли оно от планов: чистка после
 * записи плана не должна разбирать ключ строкой, формат которого живёт в
 * `ringKey` и меняется вместе с ним. */
const КЭШ = new Map<string, { supplierId: number; plans: boolean; ring: Promise<Ring> }>();

/** Стирает из кэша кольца этого поставщика, зависящие от планов: «к заказу» и
 * любое кольцо с фильтром плана или потребности.
 *
 * Вызывается после УСПЕШНОЙ записи плана на карточке: план и «к заказу» меняет
 * сам закупщик на этом же экране, и кольцо, оставшееся в кэше со старыми
 * числами, спорило бы с таблицей открытой карточки до перезагрузки страницы —
 * товар, которому только что поставили план, не появился бы в «есть план».
 * Кольца воронки без фильтров в чистку не попадают — их запись плана не трогает.
 *
 * Текущее кольцо, уже лежащее в состоянии `ProductNav`, не трогаем: порядок
 * прохода стабилен до смены настройки или ухода со страницы, как и у
 * остальных показателей. */
export function forgetPlanRings(supplierId: number): void {
  for (const [key, запись] of КЭШ) {
    if (запись.supplierId === supplierId && запись.plans) КЭШ.delete(key);
  }
}

/** Параметры кольца из настроек шестерёнки — одни у стрелок карточки и у
 * раздела «Товары»: разойдись они, страница и стрелки стояли бы в разном порядке.
 *
 * НАБОРА ПЛИТКИ ЗДЕСЬ НЕТ, и это не забывчивость: плиток на карточке не бывает,
 * а войди `show` в общие параметры — отметка «прибыль» удорожала бы КАЖДОЕ
 * кольцо листания на секунду, ничего не показывая. Набор добавляет только
 * `loadModels` (`lib/similar.ts`). */
export function ringParams(s: NavSettings, w: WindowValue): Record<string, string | number | undefined> {
  const поПотребности = s.metric === NEED_METRIC;
  return {
    // `from`/`to` ручка требует всегда, а «к заказу» их не читает.
    from: s.from,
    to: s.to,
    metric: s.metric,
    platform: поПотребности ? "both" : s.platform,
    // Сохранённое направление не трогаем: оно вернётся с воронкой.
    dir: поПотребности ? "desc" : s.dir,
    w: readsWindow(s) ? w : undefined,
    plan: s.plan,
    need: s.need,
    // Пустой параметр значит «все категории», и `query` его не печатает.
    cat: s.cat === null ? undefined : String(s.cat),
    color: s.color === null ? undefined : String(s.color),
  };
}

export function loadRing(supplierId: number, s: NavSettings, w: WindowValue): Promise<Ring> {
  const key = ringKey(supplierId, s, w);
  const было = КЭШ.get(key);
  if (было) return было.ring;
  const готовое = apiFetch<Ring>(
    "/api/demand/ranking" + query({ supplier_id: supplierId, ...ringParams(s, w) }),
  ).catch((error: unknown) => {
    // Упавший запрос из кэша выбрасываем: иначе одна сетевая ошибка
    // запомнилась бы до перезагрузки страницы.
    КЭШ.delete(key);
    throw error;
  });
  КЭШ.set(key, { supplierId, plans: readsPlans(s), ring: готовое });
  return готовое;
}
